import { faArrowUpAZ, faArrowUpZA, faSearch, faUsers } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import cn from 'classnames';
import { useSelectable } from 'hooks/useSelectable';
import useDebounce from 'hooks/useDebounce';
import { getClients, deleteClients } from '@redux/features/clientsSlice';
import { addNotification } from '@redux/features/notificationsSlice';
import { SelectionInfo } from 'components/SelectionInfo';
import { NoResults } from 'components/NoResults';
import { Checkbox } from 'components/Checkbox';
import { Button } from 'components/Button';
import { Input } from 'components/Input';
import { CreateClient } from 'pages/Database/Clients/CreateClient';
import { ClientCard } from 'pages/Database/Clients/ClientCard';
import { PageSelector } from '../PageSelector';
import mainStyles from 'pages/Database/Database.scss';
import styles from 'pages/Database/Clients/Clients.scss';

export const Clients = () => {
  const { isLoading, clients } = useSelector((state) => state.clients);
  const { selectedIDs, toggleSelection, clearSelection } = useSelectable();
  const [highlightedClientID, setHighlightedClientID] = useState(null);
  const [showOnlyWithDebt, setShowOnlyWithDebt] = useState(false);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortAsc, setSortAsc] = useState(true);
  const dispatch = useDispatch();

  const debouncedSearchQuery = useDebounce(searchQuery, 400);

  useEffect(() => {
    dispatch(getClients(debouncedSearchQuery));
  }, [debouncedSearchQuery]);

  useEffect(() => {
    if (highlightedClientID) {
      const timer = setTimeout(() => {
        setHighlightedClientID(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [highlightedClientID]);

  const handleDelete = async () => {
    if (selectedIDs.length > 0) {
      const result = await dispatch(deleteClients(selectedIDs));

      if (result.meta.requestStatus === 'fulfilled') {
        dispatch(
          addNotification({
            id: Date.now(),
            title: 'Клієнти успішно видалені',
            type: 'success',
          })
        );
        toggleSelectMode();
      }
    }
  };

  const sortedClients = [...clients].sort((a, b) => {
    const surnameCompare = a.surname.localeCompare(b.surname);
    if (surnameCompare !== 0) return sortAsc ? surnameCompare : -surnameCompare;
    const nameCompare = a.name.localeCompare(b.name);
    return sortAsc ? nameCompare : -nameCompare;
  });

  const filteredClients = showOnlyWithDebt ? sortedClients.filter((client) => client.debt > 0) : sortedClients;

  const toggleSelectMode = () => {
    setIsSelectionMode(!isSelectionMode);

    if (isSelectionMode) {
      clearSelection();
    }
  };

  return (
    <>
      <div className={styles.header}>
        <div className={styles.titleWrapper}>
          <h1>
            <FontAwesomeIcon icon={faUsers} />
            База даних
          </h1>
          <p>Огляд клієнтів, тренерів та груп</p>
        </div>
        <h1 className={styles.title}></h1>
        <PageSelector />
        <div className={styles.buttons}>
          <SelectionInfo
            selectedItems={selectedIDs}
            wordForms={['клієнта', 'клієнти', 'клієнтів']}
            isSelectionMode={isSelectionMode}
          />
          <Button
            text={isSelectionMode ? 'Відміна' : 'Виділити'}
            onClick={toggleSelectMode}
            ghostStyle={isSelectionMode}
          />
          <Button text='Видалити' onClick={handleDelete} disabled={selectedIDs.length === 0} />
        </div>
      </div>
      <div className={styles.actions}>
        <Input
          placeholder="Прізвище або ім'я"
          icon={faSearch}
          value={searchQuery}
          onChange={({ target }) => setSearchQuery(target.value)}
        />
        <button className={styles.sortBtn} onClick={() => setSortAsc(!sortAsc)}>
          <FontAwesomeIcon icon={sortAsc ? faArrowUpAZ : faArrowUpZA} />
        </button>
        <Checkbox
          label='З боргами'
          checked={showOnlyWithDebt}
          onChange={() => setShowOnlyWithDebt(!showOnlyWithDebt)}
        />
        <CreateClient onClientCreated={(client) => setHighlightedClientID(client.id)} />
      </div>
      {filteredClients.length > 0 ? (
        <ul className={cn(mainStyles.table, styles.clients)}>
          {filteredClients.map((client) => (
            <ClientCard
              client={client}
              isHighlighted={client.id === highlightedClientID}
              isSelected={selectedIDs.includes(client.id)}
              isSelectionMode={isSelectionMode}
              toggleSelection={toggleSelection}
              key={client.id}
            />
          ))}
        </ul>
      ) : (
        !isLoading && <NoResults />
      )}
    </>
  );
};
